/**
 * Check-in Form Service
 * 
 * Handles check-in form template management including:
 * - Template CRUD operations
 * - Default template management
 * - Field configuration
 */

import { db } from './firebase';
import firebase from 'firebase/compat/app';

// ============================================================================
// CHECK-IN FORM TEMPLATE MANAGEMENT
// ============================================================================

/**
 * Get all check-in templates for a clinic
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<Array>} Array of check-in templates
 */
export async function getCheckInTemplates(clinicId) {
  if (!clinicId) {
    console.warn('[CheckInFormService] No clinicId provided');
    return [];
  }
  
  try {
    const snapshot = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('checkInTemplates')
      .orderBy('createdAt', 'desc')
      .get();
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('[CheckInFormService] getCheckInTemplates:', error);
    return [];
  }
}

/**
 * Get a specific check-in template
 * @param {string} clinicId - Clinic ID
 * @param {string} templateId - Template ID
 * @returns {Promise<Object|null>} Template data or null
 */
export async function getCheckInTemplate(clinicId, templateId) {
  if (!clinicId || !templateId) {
    console.warn('[CheckInFormService] Missing clinicId or templateId');
    return null;
  }
  
  try {
    const doc = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('checkInTemplates')
      .doc(templateId)
      .get();
    
    if (!doc.exists) {
      return null;
    }
    
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('[CheckInFormService] getCheckInTemplate:', error);
    return null;
  }
}

/**
 * Get the default check-in template for a clinic
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<Object|null>} Default template or null
 */
export async function getDefaultCheckInTemplate(clinicId) {
  if (!clinicId) {
    console.warn('[CheckInFormService] No clinicId provided');
    return null;
  }
  
  try {
    const snapshot = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('checkInTemplates')
      .where('isDefault', '==', true)
      .limit(1)
      .get();
    
    if (snapshot.empty) {
      // If no default, return the first active template
      const fallbackSnapshot = await db
        .collection('clinics')
        .doc(clinicId)
        .collection('checkInTemplates')
        .where('active', '==', true)
        .limit(1)
        .get();
      
      if (fallbackSnapshot.empty) {
        return null;
      }
      
      const doc = fallbackSnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    }
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('[CheckInFormService] getDefaultCheckInTemplate:', error);
    return null;
  }
}

/**
 * Create a new check-in template
 * @param {string} clinicId - Clinic ID
 * @param {Object} templateData - Template data
 * @param {string} createdBy - User ID of creator
 * @returns {Promise<string>} Created template ID
 */
export async function createCheckInTemplate(clinicId, templateData, createdBy) {
  if (!clinicId || !templateData || !createdBy) {
    throw new Error('Missing required parameters');
  }
  
  // Check if this is the first template - make it default
  const existingTemplates = await getCheckInTemplates(clinicId);
  const isFirstTemplate = existingTemplates.length === 0;
  
  const templateDoc = {
    name: templateData.name || 'New Check-in Form',
    description: templateData.description || '',
    isDefault: isFirstTemplate || templateData.isDefault || false,
    active: templateData.active !== false,
    fields: templateData.fields || [],
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    createdBy: createdBy,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };
  
  // If setting as default, unset other defaults first
  if (templateDoc.isDefault && !isFirstTemplate) {
    await unsetAllDefaultTemplates(clinicId);
  }
  
  const docRef = await db
    .collection('clinics')
    .doc(clinicId)
    .collection('checkInTemplates')
    .add(templateDoc);
  
  return docRef.id;
}

/**
 * Update a check-in template
 * @param {string} clinicId - Clinic ID
 * @param {string} templateId - Template ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export async function updateCheckInTemplate(clinicId, templateId, updates) {
  if (!clinicId || !templateId) {
    throw new Error('Missing required parameters');
  }
  
  // If setting as default, unset other defaults first
  if (updates.isDefault === true) {
    await unsetAllDefaultTemplates(clinicId);
  }
  
  const updateData = {
    ...updates,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };
  
  // Remove undefined values
  Object.keys(updateData).forEach(key => {
    if (updateData[key] === undefined) {
      delete updateData[key];
    }
  });
  
  await db
    .collection('clinics')
    .doc(clinicId)
    .collection('checkInTemplates')
    .doc(templateId)
    .update(updateData);
  
}

/**
 * Delete a check-in template
 * @param {string} clinicId - Clinic ID
 * @param {string} templateId - Template ID
 * @returns {Promise<void>}
 */
export async function deleteCheckInTemplate(clinicId, templateId) {
  if (!clinicId || !templateId) {
    throw new Error('Missing required parameters');
  }
  
  // Check if this is the default template
  const template = await getCheckInTemplate(clinicId, templateId);
  const wasDefault = template?.isDefault;
  
  await db
    .collection('clinics')
    .doc(clinicId)
    .collection('checkInTemplates')
    .doc(templateId)
    .delete();
  
  
  // If deleted template was default, set another one as default
  if (wasDefault) {
    const remaining = await getCheckInTemplates(clinicId);
    if (remaining.length > 0) {
      await setDefaultTemplate(clinicId, remaining[0].id);
    }
  }
}

/**
 * Set a template as the default
 * @param {string} clinicId - Clinic ID
 * @param {string} templateId - Template ID to set as default
 * @returns {Promise<void>}
 */
export async function setDefaultTemplate(clinicId, templateId) {
  if (!clinicId || !templateId) {
    throw new Error('Missing required parameters');
  }
  
  // Unset all other defaults
  await unsetAllDefaultTemplates(clinicId);
  
  // Set this one as default
  await db
    .collection('clinics')
    .doc(clinicId)
    .collection('checkInTemplates')
    .doc(templateId)
    .update({
      isDefault: true,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  
}

/**
 * Helper: Unset all default templates for a clinic
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<void>}
 */
async function unsetAllDefaultTemplates(clinicId) {
  const snapshot = await db
    .collection('clinics')
    .doc(clinicId)
    .collection('checkInTemplates')
    .where('isDefault', '==', true)
    .get();
  
  const batch = db.batch();
  snapshot.docs.forEach(doc => {
    batch.update(doc.ref, { isDefault: false });
  });
  
  if (!snapshot.empty) {
    await batch.commit();
  }
}

// ============================================================================
// FIELD MANAGEMENT HELPERS
// ============================================================================

/**
 * Generate a unique field ID
 * @returns {string} Unique field ID
 */
export function generateFieldId() {
  return `field-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a patient data field object
 * @param {string} dataPath - Path to patient data (e.g., 'patient.firstName')
 * @param {string} label - Display label
 * @param {boolean} required - Whether field is required
 * @param {number} order - Display order
 * @returns {Object} Patient data field object
 */
export function createPatientDataField(dataPath, label, required = false, order = 0) {
  return {
    id: generateFieldId(),
    type: 'patientData',
    dataPath: dataPath,
    label: label,
    required: required,
    order: order,
  };
}

/**
 * Create a custom field object for check-in forms
 * @param {Object} fieldData - Custom field configuration
 * @param {number} order - Display order
 * @returns {Object} Custom field object
 */
export function createCheckInCustomField(fieldData, order = 0) {
  return {
    id: generateFieldId(),
    type: 'custom',
    fieldType: fieldData.fieldType || 'text',
    label: fieldData.label,
    options: fieldData.options || null,
    required: fieldData.required || false,
    placeholder: fieldData.placeholder || '',
    order: order,
  };
}

/**
 * Reorder fields in a check-in template
 * @param {Array} fields - Current fields array
 * @param {number} fromIndex - Index to move from
 * @param {number} toIndex - Index to move to
 * @returns {Array} Reordered fields with updated order values
 */
export function reorderCheckInFields(fields, fromIndex, toIndex) {
  const result = Array.from(fields);
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  
  // Update order values
  return result.map((field, index) => ({
    ...field,
    order: index,
  }));
}

// ============================================================================
// CHECK-IN QR CODE MANAGEMENT
// ============================================================================

// Base URL for check-in links (can be configured per environment)
const CHECK_IN_BASE_URL = 'https://hlthdsk-experimental-demo.web.app/checkin';

/**
 * Generate a unique check-in code
 * @returns {string} Unique code (8 characters alphanumeric)
 */
function generateCheckInCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed ambiguous chars like 0/O, 1/I/L
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Get check-in QR data for a specific clinic template.
 * Each template (clinic) has its own QR code so patients are directed
 * to the correct check-in form / encounter template.
 * @param {string} clinicId - Facility ID
 * @param {string} templateId - Check-in template ID
 * @returns {Promise<Object|null>} QR data or null if not generated
 */
export async function getCheckInQR(clinicId, templateId) {
  if (!clinicId || !templateId) {
    console.warn('[CheckInFormService] clinicId and templateId required for getCheckInQR');
    return null;
  }
  
  try {
    const doc = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('checkInQR')
      .doc(templateId)
      .get();
    
    if (!doc.exists) {
      return null;
    }
    
    const data = doc.data();
    if (!data.active) {
      return null;
    }
    
    return {
      code: data.code,
      checkInUrl: data.checkInUrl,
      clinicId: data.clinicId,
      templateId: data.templateId,
      generatedAt: data.generatedAt?.toDate?.() || data.generatedAt,
      generatedBy: data.generatedBy,
    };
  } catch (error) {
    console.error('[CheckInFormService] getCheckInQR:', error);
    return null;
  }
}

/**
 * Generate check-in QR code for a specific clinic template.
 * URL format: /checkin/{clinicId}/{templateId}/{code}
 * @param {string} clinicId - Facility ID
 * @param {string} templateId - Check-in template ID
 * @param {string} userId - User ID generating the code
 * @returns {Promise<Object>} QR data { code, checkInUrl, clinicId, templateId, generatedAt }
 */
export async function generateCheckInQR(clinicId, templateId, userId) {
  if (!clinicId || !templateId || !userId) {
    throw new Error('Missing required parameters: clinicId, templateId, and userId');
  }
  
  const code = generateCheckInCode();
  const checkInUrl = `${CHECK_IN_BASE_URL}/${clinicId}/${templateId}/${code}`;
  
  const qrDoc = {
    code: code,
    checkInUrl: checkInUrl,
    clinicId: clinicId,
    templateId: templateId,
    generatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    generatedBy: userId,
    active: true,
  };
  
  await db
    .collection('clinics')
    .doc(clinicId)
    .collection('checkInQR')
    .doc(templateId)
    .set(qrDoc);
  
  return {
    code: code,
    checkInUrl: checkInUrl,
    clinicId: clinicId,
    templateId: templateId,
    generatedAt: new Date(),
  };
}

/**
 * Regenerate check-in QR code for a specific clinic template (invalidates the old one)
 * @param {string} clinicId - Facility ID
 * @param {string} templateId - Check-in template ID
 * @param {string} userId - User ID regenerating the code
 * @returns {Promise<Object>} New QR data
 */
export async function regenerateCheckInQR(clinicId, templateId, userId) {
  if (!clinicId || !templateId || !userId) {
    throw new Error('Missing required parameters: clinicId, templateId, and userId');
  }
  
  // Archive the old QR code (optional - for audit trail)
  const oldDoc = await db
    .collection('clinics')
    .doc(clinicId)
    .collection('checkInQR')
    .doc(templateId)
    .get();
  
  if (oldDoc.exists) {
    const oldData = oldDoc.data();
    // Save to history with templateId prefix for clean namespacing
    await db
      .collection('clinics')
      .doc(clinicId)
      .collection('checkInQR')
      .doc(`archived-${templateId}-${Date.now()}`)
      .set({
        ...oldData,
        active: false,
        archivedAt: firebase.firestore.FieldValue.serverTimestamp(),
        archivedBy: userId,
      });
  }
  
  // Generate new QR code
  return await generateCheckInQR(clinicId, templateId, userId);
}

/**
 * Validate a check-in code for a specific clinic template.
 * @param {string} clinicId - Facility ID
 * @param {string} templateId - Check-in template ID
 * @param {string} code - Check-in code from QR/URL
 * @returns {Promise<boolean>} True if valid and active
 */
export async function validateCheckInCode(clinicId, templateId, code) {
  if (!clinicId || !templateId || !code) {
    return false;
  }
  
  try {
    const doc = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('checkInQR')
      .doc(templateId)
      .get();
    
    if (!doc.exists) {
      return false;
    }
    
    const data = doc.data();
    return data.active && data.code === code;
  } catch (error) {
    console.error('[CheckInFormService] validateCheckInCode:', error);
    return false;
  }
}

// ============================================================================
// CHECK-IN VISIT / CMR SNAPSHOT
// ============================================================================

/** Safe Firestore visit doc id segment from client (UUID or fallback). */
const CHECK_IN_CLIENT_REQUEST_ID_RE = /^[A-Za-z0-9_-]{16,128}$/;

function isValidCheckInClientRequestId(id) {
  return typeof id === 'string' && CHECK_IN_CLIENT_REQUEST_ID_RE.test(id);
}

/**
 * Create a check-in visit with CMR snapshot
 * @param {string} clinicId - Clinic ID
 * @param {string} patientId - Patient user ID
 * @param {string} templateId - Check-in template ID used
 * @param {Object} formData - Form data including patientData snapshot and customFieldResponses
 * @param {Object} formData.patientData - Patient data snapshot (CMR at check-in time)
 * @param {Object} formData.customFieldResponses - Custom field responses
 * @param {Array} formData.akaNames - Patient's AKA names for clinician reference
 * @param {Array} formData.previousDOBs - Patient's previous DOBs for clinician reference
 * @param {Array} formData.modifiedFields - Fields modified during check-in
 * @param {boolean} formData.nameChanged - Whether name was changed during check-in
 * @param {boolean} formData.dobChanged - Whether DOB was changed during check-in
 * @param {string} [formData.checkInClientRequestId] - Stable id per submit attempt (e.g. crypto.randomUUID()); when valid, used as visit doc id so retries do not duplicate visits
 * @param {*} [formData.conversationResults] - SecureLink AI intake conversation payload when present
 * @param {*} [formData.rosResults] - Structured ROS from intake when present
 * @param {*} [formData.idScan] - ID scan / OCR payload when present
 * @param {*} [formData.insuranceScan] - Insurance scan when present
 * @param {string|null} [formData.faceVerificationStatus]
 * @param {*} [formData.faceVerifiedByStaff]
 * @param {string|null} [formData.feePaymentId]
 * @param {number|null} [formData.registrationFeeKsh]
 * @param {string|null} [formData.registrationPaymentStatus]
 * @param {'qr_code'|'sms'|'targeted_invite'|string} [formData.checkInMethod] - Defaults to `qr_code`. Use `targeted_invite` when the patient arrived via a targeted remote invite link.
 * @returns {Promise<Object>} Visit record { visitId, clinicId, checkedInAt, duplicate?: boolean }
 */
export async function createCheckInVisit(clinicId, patientId, templateId, formData) {
  if (!clinicId || !patientId || !templateId) {
    throw new Error('Missing required parameters: clinicId, patientId, and templateId');
  }
  const checkInMethod = formData?.checkInMethod ?? 'qr_code';

  const visitData = {
    // Core identifiers
    clinicId: clinicId,
    patientId: patientId,
    templateId: templateId,
    
    // Timestamps
    checkedInAt: firebase.firestore.FieldValue.serverTimestamp(),
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    
    // Status
    status: 'checked_in',
    
    // CMR Snapshot - frozen patient data at check-in time
    cmrSnapshot: formData.patientData || {},
    
    // Patient history for clinician reference (included in snapshot for this visit)
    akaNames: formData.akaNames || [],
    previousDOBs: formData.previousDOBs || [],
    
    // Custom field responses
    customFieldResponses: formData.customFieldResponses || {},
    
    // Visit metadata
    checkInMethod,
    visitType: 'walk_in',
    
    // Track if data was modified during this check-in
    dataModifiedDuringCheckIn: (formData.modifiedFields?.length > 0) || false,
    modifiedFields: formData.modifiedFields || [],

    // SecureLink conversation results (from AI intake conversation, if template had one)
    conversationResults: formData.conversationResults || null,

    // ROS extracted from intake conversation (structured 10-system Review of Systems)
    rosResults: formData.rosResults || null,

    // Patient ID scan (photo ID capture with OCR-extracted name/DOB)
    idScan: formData.idScan || null,

    // Insurance card scan (insurance card capture with OCR-extracted plan/member info)
    insuranceScan: formData.insuranceScan || null,

    // Face verification (check-in): 'enrolled' | 'verified' | 'staff_override' | null
    faceVerificationStatus: formData.faceVerificationStatus || null,
    faceVerifiedByStaff: formData.faceVerifiedByStaff || null,

    // Registration/check-in fee (when paid before visit creation)
    feePaymentId: formData.feePaymentId || null,
    registrationFeeKsh: formData.registrationFeeKsh ?? null,
    registrationPaymentStatus: formData.registrationPaymentStatus || null,
  };

  const visitsCol = db.collection('clinics').doc(clinicId).collection('visits');
  const clientRequestId = formData?.checkInClientRequestId;

  if (isValidCheckInClientRequestId(clientRequestId)) {
    const visitRef = visitsCol.doc(clientRequestId);
    const result = await db.runTransaction(async (t) => {
      const snap = await t.get(visitRef);
      if (snap.exists) {
        const existing = snap.data();
        if (existing.patientId !== patientId) {
          throw new Error('This check-in request id is already in use.');
        }
        return {
          visitId: visitRef.id,
          clinicId,
          checkedInAt: existing.checkedInAt?.toDate?.() || new Date(),
          duplicate: true,
        };
      }
      t.set(visitRef, {
        ...visitData,
        checkInClientRequestId: clientRequestId,
      });
      return {
        visitId: visitRef.id,
        clinicId,
        checkedInAt: new Date(),
        duplicate: false,
      };
    });
    return result;
  }

  const visitRef = await visitsCol.add(visitData);

  return {
    visitId: visitRef.id,
    clinicId: clinicId,
    checkedInAt: new Date(),
  };
}

/**
 * Get visits for a clinic (for the queue/dashboard)
 * @param {string} clinicId - Clinic ID
 * @param {string} status - Optional status filter ('checked_in', 'in_progress', 'completed')
 * @param {number} limit - Max results (default 50)
 * @returns {Promise<Array>} Array of visit records
 */
export async function getClinicVisits(clinicId, status = null, limit = 50) {
  if (!clinicId) {
    console.warn('[CheckInFormService] No clinicId provided for getClinicVisits');
    return [];
  }

  try {
    let query = db
      .collection('clinics')
      .doc(clinicId)
      .collection('visits')
      .orderBy('checkedInAt', 'desc')
      .limit(limit);

    if (status) {
      query = query.where('status', '==', status);
    }

    const snapshot = await query.get();

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      checkedInAt: doc.data().checkedInAt?.toDate?.() || doc.data().checkedInAt,
    }));
  } catch (error) {
    console.error('[CheckInFormService] getClinicVisits:', error);
    return [];
  }
}

/**
 * Get today's visits for a clinic
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<Array>} Array of today's visit records
 */
export async function getTodayVisits(clinicId) {
  if (!clinicId) {
    return [];
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  try {
    const snapshot = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('visits')
      .where('checkedInAt', '>=', today)
      .orderBy('checkedInAt', 'desc')
      .get();

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      checkedInAt: doc.data().checkedInAt?.toDate?.() || doc.data().checkedInAt,
    }));
  } catch (error) {
    console.error('[CheckInFormService] getTodayVisits:', error);
    return [];
  }
}

/**
 * Update visit status
 * @param {string} clinicId - Clinic ID
 * @param {string} visitId - Visit ID
 * @param {string} status - New status ('checked_in', 'called', 'in_progress', 'completed', 'admitted', 'discharged', 'declined')
 * @param {Object} [options] - Additional options for specific statuses
 * @param {string} [options.declineReason] - Reason for declining (when status is 'declined')
 * @param {string} [options.statusMessage] - Custom message to display to the patient
 * @returns {Promise<void>}
 */
export async function updateVisitStatus(clinicId, visitId, status, options = {}) {
  if (!clinicId || !visitId || !status) {
    throw new Error('Missing required parameters');
  }

  const updateData = {
    status: status,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };

  // Add timestamp based on status
  if (status === 'called') {
    updateData.calledAt = firebase.firestore.FieldValue.serverTimestamp();
  } else if (status === 'in_progress') {
    updateData.inProgressAt = firebase.firestore.FieldValue.serverTimestamp();
  } else if (status === 'completed') {
    updateData.completedAt = firebase.firestore.FieldValue.serverTimestamp();
  } else if (status === 'admitted') {
    updateData.admittedAt = firebase.firestore.FieldValue.serverTimestamp();
  } else if (status === 'discharged') {
    updateData.dischargedAt = firebase.firestore.FieldValue.serverTimestamp();
  } else if (status === 'declined') {
    updateData.declinedAt = firebase.firestore.FieldValue.serverTimestamp();
    if (options.declineReason) {
      updateData.declineReason = options.declineReason;
    }
    if (options.statusMessage) {
      updateData.statusMessage = options.statusMessage;
    }
  }

  await db
    .collection('clinics')
    .doc(clinicId)
    .collection('visits')
    .doc(visitId)
    .update(updateData);

}

/**
 * Link a ChartMind draft to a visit record
 * This allows resuming the ChartMind session from the clinic queue
 * @param {string} clinicId - Clinic ID (facilityId)
 * @param {string} visitId - Visit ID
 * @param {string} draftId - ChartMind draft/encounter ID
 * @returns {Promise<void>}
 */
export async function linkChartMindDraft(clinicId, visitId, draftId) {
  if (!clinicId || !visitId || !draftId) {
    console.warn('[CheckInFormService] linkChartMindDraft: Missing required parameters', { clinicId, visitId, draftId });
    return;
  }

  try {
    await db
      .collection('clinics')
      .doc(clinicId)
      .collection('visits')
      .doc(visitId)
      .update({
        chartmindDraftId: draftId,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });

  } catch (error) {
    console.error('[CheckInFormService] linkChartMindDraft:', error);
    // Non-fatal; caller does not need to handle
  }
}

/**
 * Get a single check-in visit by ID
 * @param {string} clinicId - Clinic ID
 * @param {string} visitId - Visit ID
 * @returns {Promise<Object|null>} Visit data or null
 */
export async function getCheckInVisit(clinicId, visitId) {
  if (!clinicId || !visitId) {
    return null;
  }

  try {
    const doc = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('visits')
      .doc(visitId)
      .get();

    if (!doc.exists) {
      return null;
    }

    return {
      id: doc.id,
      ...doc.data(),
      checkedInAt: doc.data().checkedInAt?.toDate?.() || doc.data().checkedInAt,
    };
  } catch (error) {
    console.error('[CheckInFormService] getVisit:', error);
    return null;
  }
}

// ============================================================================
// CLINIC METADATA & CAREMAP INTEGRATION
// ============================================================================

/**
 * Get clinic metadata (root document)
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<Object|null>} Clinic metadata or null
 */
export async function getClinicMetadata(clinicId) {
  if (!clinicId) {
    console.warn('[CheckInFormService] No clinicId provided for getClinicMetadata');
    return null;
  }

  try {
    const doc = await db.collection('clinics').doc(clinicId).get();
    
    if (!doc.exists) {
      return null;
    }

    return {
      id: doc.id,
      ...doc.data(),
    };
  } catch (error) {
    console.error('[CheckInFormService] getClinicMetadata:', error);
    return null;
  }
}

/**
 * Update clinic metadata (root document)
 * Used for specialty, CareMap display settings, and linking to location
 * @param {string} clinicId - Clinic ID
 * @param {Object} metadata - Metadata to update
 * @param {string} metadata.clinicName - Display name for this clinic
 * @param {string[]} metadata.specialties - Array of specialty values
 * @param {boolean} metadata.displayOnCareMap - Whether to show on CareMap
 * @param {string} metadata.locationId - Link to CareMap location
 * @param {string} metadata.checkInUrl - Check-in URL for this clinic
 * @returns {Promise<void>}
 */
export async function updateClinicMetadata(clinicId, metadata) {
  if (!clinicId) {
    throw new Error('Missing required parameter: clinicId');
  }

  const updateData = {
    ...metadata,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };

  // Remove undefined values
  Object.keys(updateData).forEach(key => {
    if (updateData[key] === undefined) {
      delete updateData[key];
    }
  });

  await db.collection('clinics').doc(clinicId).set(updateData, { merge: true });


  // If displayOnCareMap is set and we have a locationId, sync to location
  if (metadata.locationId) {
    await syncClinicToLocation(clinicId, metadata.locationId);
  }
}

/**
 * Sync clinic data to its CareMap location document
 * Aggregates all clinics' specialties and check-in links to the location
 * @param {string} clinicId - Clinic ID that was updated
 * @param {string} locationId - Location ID to sync to
 * @returns {Promise<void>}
 */
export async function syncClinicToLocation(clinicId, locationId) {
  if (!clinicId || !locationId) {
    console.warn('[CheckInFormService] Missing clinicId or locationId for sync');
    return;
  }

  try {
    // Get all clinics linked to this location that want to display on CareMap
    const clinicsSnapshot = await db
      .collection('clinics')
      .where('locationId', '==', locationId)
      .where('displayOnCareMap', '==', true)
      .get();

    // Aggregate specialties and check-in links
    const allSpecialties = new Set();
    const checkInClinics = [];

    clinicsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      
      // Add specialties to set
      if (data.specialties && Array.isArray(data.specialties)) {
        data.specialties.forEach(s => allSpecialties.add(s));
      }

      // Add check-in clinic entry
      if (data.checkInUrl && data.clinicName) {
        checkInClinics.push({
          clinicId: doc.id,
          clinicName: data.clinicName,
          specialties: data.specialties || [],
          checkInUrl: data.checkInUrl,
        });
      }
    });

    // Update the location document
    await db.collection('locations').doc(locationId).update({
      specialties: Array.from(allSpecialties),
      checkInClinics: checkInClinics,
      checkInUpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

  } catch (error) {
    console.error('[CheckInFormService] syncClinicToLocation:', error);
    throw error;
  }
}

/**
 * Get all clinics linked to a location
 * @param {string} locationId - Location ID
 * @returns {Promise<Array>} Array of clinic metadata
 */
export async function getClinicsByLocation(locationId) {
  if (!locationId) {
    return [];
  }

  try {
    const snapshot = await db
      .collection('clinics')
      .where('locationId', '==', locationId)
      .get();

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('[CheckInFormService] getClinicsByLocation:', error);
    return [];
  }
}

/**
 * Remove clinic from CareMap (sets displayOnCareMap to false and re-syncs)
 * @param {string} clinicId - Clinic ID
 * @param {string} locationId - Location ID
 * @returns {Promise<void>}
 */
export async function removeClinicFromCareMap(clinicId, locationId) {
  if (!clinicId) {
    throw new Error('Missing required parameter: clinicId');
  }

  // Update clinic to not display on CareMap
  await db.collection('clinics').doc(clinicId).update({
    displayOnCareMap: false,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  });

  // Re-sync location to remove this clinic's data
  if (locationId) {
    await syncClinicToLocation(clinicId, locationId);
  }

}

// ============================================================================
// DEPARTMENT MANAGEMENT
// ============================================================================

/**
 * Get all departments for a facility
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Array>} Array of departments
 */
export async function getDepartments(facilityId) {
  if (!facilityId) {
    console.warn('[CheckInFormService] No facilityId provided for getDepartments');
    return [];
  }

  try {
    const snapshot = await db
      .collection('clinics')
      .doc(facilityId)
      .collection('departments')
      .orderBy('name', 'asc')
      .get();

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('[CheckInFormService] getDepartments:', error);
    return [];
  }
}

/**
 * Get a specific department
 * @param {string} facilityId - Facility ID
 * @param {string} departmentId - Department ID
 * @returns {Promise<Object|null>} Department data or null
 */
export async function getDepartment(facilityId, departmentId) {
  if (!facilityId || !departmentId) {
    return null;
  }

  try {
    const doc = await db
      .collection('clinics')
      .doc(facilityId)
      .collection('departments')
      .doc(departmentId)
      .get();

    if (!doc.exists) {
      return null;
    }

    return {
      id: doc.id,
      ...doc.data(),
    };
  } catch (error) {
    console.error('[CheckInFormService] getDepartment:', error);
    return null;
  }
}

/**
 * Create a new department
 * @param {string} facilityId - Facility ID
 * @param {Object} departmentData - Department data
 * @param {string} departmentData.name - Department name
 * @param {string} departmentData.specialty - Default specialty for clinics in this department
 * @param {string} createdBy - User ID who created the department
 * @returns {Promise<string>} New department ID
 */
export async function createDepartment(facilityId, departmentData, createdBy) {
  if (!facilityId || !departmentData?.name) {
    throw new Error('Missing required parameters: facilityId and name');
  }

  const docRef = await db
    .collection('clinics')
    .doc(facilityId)
    .collection('departments')
    .add({
      name: departmentData.name,
      specialty: departmentData.specialty || null,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      createdBy: createdBy,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

  return docRef.id;
}

/**
 * Update a department
 * @param {string} facilityId - Facility ID
 * @param {string} departmentId - Department ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export async function updateDepartment(facilityId, departmentId, updates) {
  if (!facilityId || !departmentId) {
    throw new Error('Missing required parameters');
  }

  const updateData = {
    ...updates,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };

  // Remove undefined values
  Object.keys(updateData).forEach(key => {
    if (updateData[key] === undefined) {
      delete updateData[key];
    }
  });

  await db
    .collection('clinics')
    .doc(facilityId)
    .collection('departments')
    .doc(departmentId)
    .update(updateData);

}

/**
 * Delete a department
 * @param {string} facilityId - Facility ID
 * @param {string} departmentId - Department ID
 * @returns {Promise<void>}
 */
export async function deleteDepartment(facilityId, departmentId) {
  if (!facilityId || !departmentId) {
    throw new Error('Missing required parameters');
  }

  // Check if any clinics are assigned to this department
  const clinicsSnapshot = await db
    .collection('clinics')
    .doc(facilityId)
    .collection('checkInTemplates')
    .where('departmentId', '==', departmentId)
    .limit(1)
    .get();

  if (!clinicsSnapshot.empty) {
    throw new Error('Cannot delete department with assigned clinics. Reassign clinics first.');
  }

  await db
    .collection('clinics')
    .doc(facilityId)
    .collection('departments')
    .doc(departmentId)
    .delete();

}

/**
 * Get or create the "General" department for a facility
 * Used for migration and default assignment
 * @param {string} facilityId - Facility ID
 * @param {string} createdBy - User ID for creation if needed
 * @returns {Promise<Object>} General department
 */
export async function getOrCreateGeneralDepartment(facilityId, createdBy) {
  if (!facilityId) {
    throw new Error('Missing required parameter: facilityId');
  }

  // Try to find existing General department
  const snapshot = await db
    .collection('clinics')
    .doc(facilityId)
    .collection('departments')
    .where('name', '==', 'General')
    .limit(1)
    .get();

  if (!snapshot.empty) {
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    };
  }

  // Create General department
  const departmentId = await createDepartment(facilityId, {
    name: 'General',
    specialty: null,
  }, createdBy || 'system');

  return {
    id: departmentId,
    name: 'General',
    specialty: null,
  };
}

/**
 * Get all clinics (templates) in a department
 * @param {string} facilityId - Facility ID
 * @param {string} departmentId - Department ID
 * @returns {Promise<Array>} Array of clinics
 */
export async function getClinicsByDepartment(facilityId, departmentId) {
  if (!facilityId || !departmentId) {
    return [];
  }

  try {
    const snapshot = await db
      .collection('clinics')
      .doc(facilityId)
      .collection('checkInTemplates')
      .where('departmentId', '==', departmentId)
      .orderBy('name', 'asc')
      .get();

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('[CheckInFormService] getClinicsByDepartment:', error);
    return [];
  }
}

/**
 * Assign a clinic to a department
 * Also updates clinic specialty to inherit from department if not set
 * @param {string} facilityId - Facility ID
 * @param {string} clinicId - Clinic (template) ID
 * @param {string} departmentId - Department ID
 * @param {boolean} inheritSpecialty - Whether to inherit department's specialty
 * @returns {Promise<void>}
 */
export async function assignClinicToDepartment(facilityId, clinicId, departmentId, inheritSpecialty = true) {
  if (!facilityId || !clinicId || !departmentId) {
    throw new Error('Missing required parameters');
  }

  const updateData = {
    departmentId: departmentId,
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };

  // If inheriting specialty, get department's specialty
  if (inheritSpecialty) {
    const department = await getDepartment(facilityId, departmentId);
    if (department?.specialty) {
      // Check if clinic already has specialties
      const clinicDoc = await db
        .collection('clinics')
        .doc(facilityId)
        .collection('checkInTemplates')
        .doc(clinicId)
        .get();
      
      const clinicData = clinicDoc.data();
      if (!clinicData?.specialties || clinicData.specialties.length === 0) {
        updateData.specialties = [department.specialty];
      }
    }
  }

  await db
    .collection('clinics')
    .doc(facilityId)
    .collection('checkInTemplates')
    .doc(clinicId)
    .update(updateData);

}

/**
 * Migrate orphan clinics (no departmentId) to General department
 * @param {string} facilityId - Facility ID
 * @param {string} userId - User ID performing migration
 * @returns {Promise<number>} Number of clinics migrated
 */
export async function migrateOrphanClinics(facilityId, userId) {
  if (!facilityId) {
    throw new Error('Missing required parameter: facilityId');
  }

  // Get or create General department
  const generalDept = await getOrCreateGeneralDepartment(facilityId, userId);

  // Find clinics without departmentId
  const snapshot = await db
    .collection('clinics')
    .doc(facilityId)
    .collection('checkInTemplates')
    .get();

  const batch = db.batch();
  let migratedCount = 0;

  snapshot.docs.forEach(doc => {
    const data = doc.data();
    if (!data.departmentId) {
      batch.update(doc.ref, {
        departmentId: generalDept.id,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      migratedCount++;
    }
  });

  if (migratedCount > 0) {
    await batch.commit();
  }

  return migratedCount;
}

// ============================================================================
// CHECK-IN AVAILABILITY VALIDATION
// ============================================================================

/**
 * Check if a clinic is currently open for check-ins
 * @param {string} clinicId - Clinic ID
 * @param {Date} checkTime - Time to check (defaults to now)
 * @returns {Promise<Object>} { isOpen: boolean, reason: string, nextOpen?: Date }
 */
export async function isClinicOpenForCheckIn(clinicId, checkTime = new Date()) {
  if (!clinicId) {
    return { isOpen: false, reason: 'Invalid clinic ID' };
  }

  try {
    const metadata = await getClinicMetadata(clinicId);
    if (!metadata) {
      // No metadata = no restrictions, allow check-in
      return { isOpen: true, reason: 'No operating hours configured' };
    }

    // Check for scheduled closures first
    const dateStr = checkTime.toISOString().split('T')[0]; // YYYY-MM-DD
    if (metadata.closureExceptions?.length > 0) {
      const closure = metadata.closureExceptions.find(c => c.date === dateStr);
      if (closure) {
        return { 
          isOpen: false, 
          reason: `Clinic is closed today: ${closure.reason || 'Scheduled closure'}`,
          closureReason: closure.reason,
        };
      }
    }

    // If 24/7, always open (unless closure exception above)
    if (metadata.is24x7) {
      return { isOpen: true, reason: 'Open 24/7' };
    }

    // Check weekly schedule
    if (!metadata.weeklyHours) {
      // No weekly hours configured = open
      return { isOpen: true, reason: 'No operating hours configured' };
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = dayNames[checkTime.getDay()];
    const dayHours = metadata.weeklyHours[dayOfWeek];

    if (!dayHours || !dayHours.isOpen) {
      // Find next open day
      const nextOpen = findNextOpenDay(metadata.weeklyHours, checkTime);
      return { 
        isOpen: false, 
        reason: `Clinic is closed on ${dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1)}s`,
        nextOpen,
      };
    }

    // Check if within operating hours
    const currentTime = `${String(checkTime.getHours()).padStart(2, '0')}:${String(checkTime.getMinutes()).padStart(2, '0')}`;
    
    if (currentTime < dayHours.openTime) {
      return { 
        isOpen: false, 
        reason: `Clinic opens at ${formatTime(dayHours.openTime)}`,
        opensAt: dayHours.openTime,
      };
    }

    if (currentTime >= dayHours.closeTime) {
      const nextOpen = findNextOpenDay(metadata.weeklyHours, checkTime, true);
      return { 
        isOpen: false, 
        reason: `Clinic closed at ${formatTime(dayHours.closeTime)}`,
        nextOpen,
      };
    }

    // Check if currently on break
    if (dayHours.breakStart && dayHours.breakEnd) {
      if (currentTime >= dayHours.breakStart && currentTime < dayHours.breakEnd) {
        return { 
          isOpen: false, 
          reason: `On break until ${formatTime(dayHours.breakEnd)}`,
          opensAt: dayHours.breakEnd,
        };
      }
    }

    // Check session capacity if by appointment only
    if (metadata.byAppointmentOnly) {
      const hasBreak = dayHours.breakStart && dayHours.breakEnd;
      const isAfternoon = hasBreak && currentTime >= dayHours.breakEnd;
      const sessionKey = isAfternoon ? 'afternoon' : 'morning';
      const sessionCapacity = isAfternoon 
        ? (dayHours.afternoonCapacity || 0)
        : (dayHours.morningCapacity || 0);
      
      // If capacity is 0, sessions are effectively unlimited
      if (sessionCapacity > 0) {
        // Count today's check-ins for this session
        const sessionCount = await getSessionCheckInCount(clinicId, checkTime, hasBreak ? dayHours.breakStart : null, isAfternoon);
        
        if (sessionCount >= sessionCapacity) {
          // Session is full
          if (hasBreak && !isAfternoon && dayHours.afternoonCapacity > 0) {
            // Morning is full but there's an afternoon session
            const afternoonCount = await getSessionCheckInCount(clinicId, checkTime, dayHours.breakStart, true);
            if (afternoonCount < dayHours.afternoonCapacity) {
              return {
                isOpen: false,
                reason: `Morning session is full (${sessionCount}/${sessionCapacity}). Afternoon appointments available starting at ${formatTime(dayHours.breakEnd)}.`,
                sessionFull: 'morning',
                nextSessionAt: dayHours.breakEnd,
                currentCount: sessionCount,
                capacity: sessionCapacity,
              };
            }
          }
          // Both sessions full or no afternoon session
          const nextOpen = findNextOpenDay(metadata.weeklyHours, checkTime, true);
          return {
            isOpen: false,
            reason: `Today's ${hasBreak ? (isAfternoon ? 'afternoon' : 'morning') : ''} appointments are full (${sessionCount}/${sessionCapacity}). Please try again tomorrow.`,
            sessionFull: sessionKey,
            nextOpen,
            currentCount: sessionCount,
            capacity: sessionCapacity,
          };
        }
        
        // Session has availability
        return { 
          isOpen: true, 
          reason: `Open now - ${sessionCapacity - sessionCount} ${hasBreak ? sessionKey : ''} appointment${sessionCapacity - sessionCount !== 1 ? 's' : ''} remaining`,
          closesAt: dayHours.closeTime,
          session: sessionKey,
          currentCount: sessionCount,
          capacity: sessionCapacity,
          remainingSlots: sessionCapacity - sessionCount,
        };
      }
    }

    // All checks passed - clinic is open
    return { 
      isOpen: true, 
      reason: 'Open now',
      closesAt: dayHours.closeTime,
    };
  } catch (error) {
    console.error('[CheckInFormService] checkClinicHours:', error);
    // On error, allow check-in (fail open)
    return { isOpen: true, reason: 'Unable to verify hours - check-in allowed' };
  }
}

/**
 * Count check-ins for a specific session (morning or afternoon) on a given date
 * @param {string} clinicId - Clinic ID
 * @param {Date} date - Date to check
 * @param {string|null} breakTime - Break start time (HH:MM) that divides sessions, or null if no break
 * @param {boolean} isAfternoon - Whether to count afternoon session (after break) or morning (before break)
 * @returns {Promise<number>} Number of check-ins for that session
 */
async function getSessionCheckInCount(clinicId, date, breakTime, isAfternoon) {
  if (!clinicId) return 0;

  try {
    // Get start and end of the target date
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    // Query all check-ins for this clinic on this date
    const snapshot = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('visits')
      .where('checkedInAt', '>=', startOfDay)
      .where('checkedInAt', '<=', endOfDay)
      .where('status', '!=', 'cancelled')
      .get();

    if (!breakTime) {
      // No break = count all check-ins for the day
      return snapshot.size;
    }

    // Parse break time to filter by session
    const [breakHour, breakMinute] = breakTime.split(':').map(Number);
    
    let sessionCount = 0;
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const checkedInAt = data.checkedInAt?.toDate?.() || new Date(data.checkedInAt);
      const checkInHour = checkedInAt.getHours();
      const checkInMinute = checkedInAt.getMinutes();
      const checkInTimeValue = checkInHour * 60 + checkInMinute;
      const breakTimeValue = breakHour * 60 + breakMinute;

      if (isAfternoon) {
        // Count check-ins at or after break time
        if (checkInTimeValue >= breakTimeValue) {
          sessionCount++;
        }
      } else {
        // Count check-ins before break time
        if (checkInTimeValue < breakTimeValue) {
          sessionCount++;
        }
      }
    });

    return sessionCount;
  } catch (error) {
    console.error('[CheckInFormService] Error counting session check-ins:', error);
    return 0;
  }
}

/**
 * Find the next day the clinic is open
 * @param {Object} weeklyHours - Weekly hours object
 * @param {Date} fromDate - Date to start searching from
 * @param {boolean} skipToday - Whether to skip to the next day
 * @returns {Date|null} Next open date/time or null
 */
function findNextOpenDay(weeklyHours, fromDate, skipToday = false) {
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  
  for (let i = skipToday ? 1 : 0; i < 7; i++) {
    const checkDate = new Date(fromDate);
    checkDate.setDate(checkDate.getDate() + i);
    const dayName = dayNames[checkDate.getDay()];
    const dayHours = weeklyHours[dayName];
    
    if (dayHours?.isOpen && dayHours.openTime) {
      const [hours, minutes] = dayHours.openTime.split(':').map(Number);
      checkDate.setHours(hours, minutes, 0, 0);
      return checkDate;
    }
  }
  return null;
}

/**
 * Format time string for display (e.g., "09:00" -> "9:00 AM")
 * @param {string} timeStr - Time string in HH:MM format
 * @returns {string} Formatted time string
 */
function formatTime(timeStr) {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}

/**
 * Get formatted operating hours for display
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<Object>} Formatted hours object
 */
export async function getFormattedOperatingHours(clinicId) {
  if (!clinicId) return null;

  try {
    const metadata = await getClinicMetadata(clinicId);
    if (!metadata) return null;

    if (metadata.is24x7) {
      return { display: 'Open 24/7', is24x7: true };
    }

    if (!metadata.weeklyHours) {
      return { display: 'Hours not specified', weeklyHours: null };
    }

    const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const formattedDays = {};
    
    dayNames.forEach(day => {
      const hours = metadata.weeklyHours[day];
      if (!hours || !hours.isOpen) {
        formattedDays[day] = 'Closed';
      } else {
        let display = `${formatTime(hours.openTime)} - ${formatTime(hours.closeTime)}`;
        if (hours.breakStart && hours.breakEnd) {
          display += ` (Break: ${formatTime(hours.breakStart)} - ${formatTime(hours.breakEnd)})`;
        }
        formattedDays[day] = display;
      }
    });

    return {
      display: 'See hours below',
      weeklyHours: formattedDays,
      byAppointmentOnly: metadata.byAppointmentOnly || false,
      closureExceptions: metadata.closureExceptions || [],
    };
  } catch (error) {
    console.error('[CheckInFormService] Error getting formatted hours:', error);
    return null;
  }
}

/**
 * Get session availability summary for a clinic today
 * Used for displaying remaining slots in the queue dashboard
 * @param {string} clinicId - Clinic ID
 * @returns {Promise<Object|null>} Session availability info or null
 */
export async function getSessionAvailability(clinicId) {
  if (!clinicId) return null;

  try {
    const metadata = await getClinicMetadata(clinicId);
    if (!metadata || !metadata.byAppointmentOnly) {
      return { byAppointmentOnly: false };
    }

    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = dayNames[now.getDay()];
    const dayHours = metadata.weeklyHours?.[dayOfWeek];

    if (!dayHours?.isOpen) {
      return { byAppointmentOnly: true, isOpen: false, dayOfWeek };
    }

    const hasBreak = dayHours.breakStart && dayHours.breakEnd;
    const morningCapacity = dayHours.morningCapacity || 0;
    const afternoonCapacity = dayHours.afternoonCapacity || 0;

    // Count current check-ins
    const morningCount = await getSessionCheckInCount(clinicId, now, hasBreak ? dayHours.breakStart : null, false);
    const afternoonCount = hasBreak ? await getSessionCheckInCount(clinicId, now, dayHours.breakStart, true) : 0;

    return {
      byAppointmentOnly: true,
      isOpen: true,
      dayOfWeek,
      hasBreak,
      morning: {
        capacity: morningCapacity,
        count: morningCount,
        remaining: Math.max(0, morningCapacity - morningCount),
        isFull: morningCapacity > 0 && morningCount >= morningCapacity,
        timeRange: `${formatTime(dayHours.openTime)} - ${hasBreak ? formatTime(dayHours.breakStart) : formatTime(dayHours.closeTime)}`,
      },
      afternoon: hasBreak ? {
        capacity: afternoonCapacity,
        count: afternoonCount,
        remaining: Math.max(0, afternoonCapacity - afternoonCount),
        isFull: afternoonCapacity > 0 && afternoonCount >= afternoonCapacity,
        timeRange: `${formatTime(dayHours.breakEnd)} - ${formatTime(dayHours.closeTime)}`,
      } : null,
      totalCapacity: hasBreak ? morningCapacity + afternoonCapacity : morningCapacity,
      totalCount: morningCount + afternoonCount,
      totalRemaining: hasBreak 
        ? Math.max(0, morningCapacity - morningCount) + Math.max(0, afternoonCapacity - afternoonCount)
        : Math.max(0, morningCapacity - morningCount),
    };
  } catch (error) {
    console.error('[CheckInFormService] Error getting session availability:', error);
    return null;
  }
}

// ============================================================================
// PATIENT QUEUE VIEW
// ============================================================================

/**
 * Get a patient's active (non-completed) visit for today
 * @param {string} patientId - Patient user ID
 * @returns {Promise<Object|null>} Active visit data or null
 */
export async function getPatientActiveVisit(patientId) {
  if (!patientId) {
    console.warn('[CheckInFormService] No patientId provided for getPatientActiveVisit');
    return null;
  }

  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Query all clinics for this patient's visits today
    // Note: This requires a collection group query
    const snapshot = await db
      .collectionGroup('visits')
      .where('patientId', '==', patientId)
      .where('checkedInAt', '>=', today)
      .where('status', 'in', ['checked_in', 'called', 'in_progress'])
      .orderBy('checkedInAt', 'desc')
      .limit(1)
      .get();

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

    return {
      visitId: doc.id,
      clinicId: data.clinicId,
      patientId: data.patientId,
      status: data.status,
      checkedInAt: data.checkedInAt?.toDate?.() || data.checkedInAt,
      calledAt: data.calledAt?.toDate?.() || data.calledAt,
    };
  } catch (error) {
    console.error('[CheckInFormService] Error fetching patient active visit:', error);
    return null;
  }
}

/**
 * Subscribe to real-time updates for a clinic's queue
 * @param {string} clinicId - Clinic ID
 * @param {Function} callback - Callback function receiving queue updates
 * @returns {Function} Unsubscribe function
 */
export function subscribeToClinicQueue(clinicId, callback) {
  if (!clinicId) {
    console.warn('[CheckInFormService] No clinicId provided for subscribeToClinicQueue');
    return () => {};
  }

  // Get start of today for filtering
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTimestamp = today.getTime();

  // Subscribe to visits for this clinic
  // Note: We fetch all and filter client-side to avoid composite index requirements
  const unsubscribe = db
    .collection('clinics')
    .doc(clinicId)
    .collection('visits')
    .orderBy('checkedInAt', 'asc')
    .onSnapshot(
      (snapshot) => {
        
        const visits = snapshot.docs.map((doc) => {
          const data = doc.data();
          const checkedInAt = data.checkedInAt?.toDate?.() || (data.checkedInAt ? new Date(data.checkedInAt) : null);
          return {
            id: doc.id,
            patientId: data.patientId,
            status: data.status,
            checkedInAt: checkedInAt,
            calledAt: data.calledAt?.toDate?.() || data.calledAt,
          };
        });

        // Filter to only today's active visits (not completed or cancelled)
        const activeVisits = visits.filter(v => {
          const isToday = v.checkedInAt && v.checkedInAt.getTime() >= todayTimestamp;
          const isActive = v.status === 'checked_in' || v.status === 'called' || v.status === 'in_progress';
          return isToday && isActive;
        });


        // Sort by check-in time and assign positions
        activeVisits.sort((a, b) => (a.checkedInAt?.getTime() || 0) - (b.checkedInAt?.getTime() || 0));
        
        const queueWithPositions = activeVisits.map((visit, index) => ({
          ...visit,
          position: index + 1,
        }));

        callback(queueWithPositions);
      },
      (error) => {
        console.error('[CheckInFormService] Error in queue subscription:', error);
        callback([]);
      }
    );

  return unsubscribe;
}

/**
 * Cancel/leave a check-in visit
 * @param {string} clinicId - Clinic ID
 * @param {string} visitId - Visit ID
 * @returns {Promise<void>}
 */
export async function cancelCheckInVisit(clinicId, visitId) {
  if (!clinicId || !visitId) {
    throw new Error('Missing required parameters: clinicId and visitId');
  }

  await db
    .collection('clinics')
    .doc(clinicId)
    .collection('visits')
    .doc(visitId)
    .update({
      status: 'cancelled',
      cancelledAt: firebase.firestore.FieldValue.serverTimestamp(),
      cancelledBy: 'patient',
    });

}

// ============================================================================
// PROVIDER APPROVAL (Phase 3 - clinic ChartMind access)
// ============================================================================

const PROVIDER_APPROVAL_DOC_ID = 'current';

/**
 * Get provider approval config for a clinic.
 * @param {string} clinicId
 * @returns {Promise<{ policy: string, allowedProviderIds: string[], expiryDays?: number, customMaxDays?: number }>}
 */
export async function getProviderApproval(clinicId) {
  if (!clinicId) return { policy: 'approve_if_on_list', allowedProviderIds: [] };
  try {
    const doc = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('providerApproval')
      .doc(PROVIDER_APPROVAL_DOC_ID)
      .get();
    if (!doc.exists) return { policy: 'approve_if_on_list', allowedProviderIds: [] };
    const d = doc.data();
    return {
      policy: d.policy || 'approve_if_on_list',
      allowedProviderIds: Array.isArray(d.allowedProviderIds) ? d.allowedProviderIds : [],
      expiryDays: d.expiryDays,
      customMaxDays: d.customMaxDays,
    };
  } catch (err) {
    console.warn('[CheckInFormService] getProviderApproval failed:', err);
    return { policy: 'approve_if_on_list', allowedProviderIds: [] };
  }
}

/**
 * Set provider approval config. Callable by chartmind-supervisor and facility (Firestore rules).
 * @param {string} clinicId
 * @param {Object} config - { policy, allowedProviderIds?, expiryDays?, customMaxDays? }
 */
export async function setProviderApproval(clinicId, config) {
  if (!clinicId) throw new Error('clinicId required');
  const ref = db.collection('clinics').doc(clinicId).collection('providerApproval').doc(PROVIDER_APPROVAL_DOC_ID);
  const update = {
    policy: config.policy || 'approve_if_on_list',
    allowedProviderIds: Array.isArray(config.allowedProviderIds) ? config.allowedProviderIds : [],
  };
  if (config.expiryDays !== undefined) update.expiryDays = config.expiryDays;
  if (config.customMaxDays !== undefined) update.customMaxDays = config.customMaxDays;
  await ref.set(update, { merge: true });
}

// ============================================================================
// CLINIC REGISTRARS (Phase 4 - facility assigns per clinic)
// ============================================================================

/**
 * Get assigned registrars for a clinic.
 * @param {string} clinicId
 * @returns {Promise<Array<{ userId: string, assignedAt: any, assignedBy: string }>>}
 */
export async function getRegistrars(clinicId) {
  if (!clinicId) return [];
  try {
    const snapshot = await db.collection('clinics').doc(clinicId).collection('registrars').get();
    return snapshot.docs.map((doc) => {
      const data = doc.data() || {};
      return {
        userId: doc.id,
        assignedAt: data.assignedAt,
        assignedBy: data.assignedBy ?? '',
      };
    });
  } catch (err) {
    console.warn('[CheckInFormService] getRegistrars failed:', err);
    return [];
  }
}

/**
 * Assign a user as registrar for the clinic. Callable by facility (Firestore rules).
 * @param {string} clinicId
 * @param {string} userId
 * @param {string} assignedBy - Current user UID
 */
export async function assignRegistrar(clinicId, userId, assignedBy) {
  if (!clinicId || !userId) throw new Error('clinicId and userId required');
  const ref = db.collection('clinics').doc(clinicId).collection('registrars').doc(userId);
  await ref.set({
    assignedAt: firebase.firestore.FieldValue.serverTimestamp(),
    assignedBy: assignedBy || null,
  }, { merge: true });
}

/**
 * Remove a registrar assignment.
 * @param {string} clinicId
 * @param {string} userId
 */
export async function removeRegistrar(clinicId, userId) {
  if (!clinicId || !userId) throw new Error('clinicId and userId required');
  await db.collection('clinics').doc(clinicId).collection('registrars').doc(userId).delete();
}

// ============================================================================
// REGISTRAR CONFIG (Phase 4 - subset editable by clinic registrar)
// ============================================================================

const REGISTRAR_CONFIG_DOC_ID = 'current';

/**
 * Get registrar config (welcome message, etc.) for a clinic.
 * @param {string} clinicId
 * @returns {Promise<{ welcomeMessage?: string, instructions?: string }>}
 */
export async function getRegistrarConfig(clinicId) {
  if (!clinicId) return {};
  try {
    const doc = await db
      .collection('clinics')
      .doc(clinicId)
      .collection('registrarConfig')
      .doc(REGISTRAR_CONFIG_DOC_ID)
      .get();
    return doc.exists ? (doc.data() || {}) : {};
  } catch (err) {
    console.warn('[CheckInFormService] getRegistrarConfig failed:', err);
    return {};
  }
}

/**
 * Set registrar config (subset fields). Callable by chartmind-supervisor or clinic-registrar (Firestore rules).
 * @param {string} clinicId
 * @param {Object} data - { welcomeMessage?, instructions? }
 */
export async function setRegistrarConfig(clinicId, data) {
  if (!clinicId) throw new Error('clinicId required');
  const ref = db.collection('clinics').doc(clinicId).collection('registrarConfig').doc(REGISTRAR_CONFIG_DOC_ID);
  const update = {};
  if (data.welcomeMessage !== undefined) update.welcomeMessage = data.welcomeMessage;
  if (data.instructions !== undefined) update.instructions = data.instructions;
  if (Object.keys(update).length === 0) return;
  await ref.set(update, { merge: true });
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  getCheckInTemplates,
  getCheckInTemplate,
  getDefaultCheckInTemplate,
  createCheckInTemplate,
  updateCheckInTemplate,
  deleteCheckInTemplate,
  setDefaultTemplate,
  generateFieldId,
  createPatientDataField,
  createCheckInCustomField,
  reorderCheckInFields,
  getCheckInQR,
  generateCheckInQR,
  regenerateCheckInQR,
  validateCheckInCode,
  createCheckInVisit,
  getClinicVisits,
  getTodayVisits,
  updateVisitStatus,
  getCheckInVisit,
  getProviderApproval,
  setProviderApproval,
  getRegistrars,
  assignRegistrar,
  removeRegistrar,
  getRegistrarConfig,
  setRegistrarConfig,
  // Clinic metadata & CareMap integration
  getClinicMetadata,
  updateClinicMetadata,
  syncClinicToLocation,
  getClinicsByLocation,
  removeClinicFromCareMap,
  // Department management
  getDepartments,
  getDepartment,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  getOrCreateGeneralDepartment,
  getClinicsByDepartment,
  assignClinicToDepartment,
  migrateOrphanClinics,
  // Check-in availability
  isClinicOpenForCheckIn,
  getFormattedOperatingHours,
  getSessionAvailability,
  // Patient queue view
  getPatientActiveVisit,
  subscribeToClinicQueue,
  cancelCheckInVisit,
};
